import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";

import { CustomColumnTypes } from "@/enum/list.enum";

import { useGetColumnTemplatesWithAiSnippetQuery } from "@/pages/Private/redux/list/list.api";

import { ColumnConfig } from "../../../List/schema/columnConfig";
import { List } from "../../../List/schema/list";

import { MyNode } from "../../pages/CampaignMessagingOverview/CampaignMessagingOverview";
import {
	EmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
	LinkedinInmailNodeConfig,
} from "../../../List/components/CampaignOverview/components/nodes";
import { LinkedinVisitNodeConfig } from "../../../List/components/CampaignOverview/components/nodes/LinkedinVisitNode";

import { AiSnippetsModal } from "./AiSnippetsModal";
import { AiSnippetsSectionCard } from "./components/AiSnippetsSectionCard";
import { useEditorCursorInsertion } from "./hooks/useEditorCursorInsertion";
import { useTemplateColumnInsert } from "./hooks/useTemplateColumnInsert";
import {
	rewriteAllTinyMceEditors,
	updateColumnPlaceholdersInTinyMce,
} from "./utils/rewriteEditorContent";
import { buildSnippetRows, SnippetRow } from "./utils/snippetRows";
import { buildSnippetToken, extractInsertedColumnIds } from "./utils/snippetTokens";

export interface AiSnippetsHandle {
	openCreateModal: () => void;
}

interface AiSnippetsProps {
	listData?: List;
	selectedNode?:
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinInmailNodeConfig
		| LinkedinVisitNodeConfig;
	withPersistance?: (result: string[]) => void;
}

export const AiSnippets = forwardRef<AiSnippetsHandle, AiSnippetsProps>(function AiSnippets(
	{ listData, selectedNode, withPersistance },
	ref
) {
	const { insertAtCursor, captureCurrentPosition } = useEditorCursorInsertion();

	const [showModal, setShowModal] = useState<boolean>(false);
	const [modalInitialColumn, setModalInitialColumn] = useState<ColumnConfig | null>(null);

	const { data: templatesResponse } = useGetColumnTemplatesWithAiSnippetQuery({
		page: 1,
		limit: 1000,
	});

	const allAiSnippets = useMemo(
		() => listData?.columns?.filter((c) => c.type === CustomColumnTypes.AI_SNIPPET) ?? [],
		[listData?.columns]
	);

	const message = useMemo(() => {
		return selectedNode?.data?.message ?? "";
	}, [selectedNode?.data?.message]);

	const premiumMessage = useMemo(() => {
		return selectedNode?.data?.premiumMessage ?? "";
	}, [selectedNode?.data?.premiumMessage]);

	const handleOpenModal = useCallback(
		(column: ColumnConfig | null) => {
			captureCurrentPosition();
			setModalInitialColumn(column);
			setShowModal(true);
		},
		[captureCurrentPosition]
	);

	const handleCloseModal = useCallback(() => {
		setShowModal(false);
		setModalInitialColumn(null);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			openCreateModal: () => handleOpenModal(null),
		}),
		[handleOpenModal]
	);

	const handleInsertToken = useCallback(
		(token: string): boolean => {
			const ok = insertAtCursor(token);

			if (!ok && withPersistance) {
				// Fallback: append to the message.
				const next = message ? `${message} ${token}` : token;

				withPersistance([next]);
			}

			return ok;
		},
		[insertAtCursor, withPersistance, message]
	);

	const { insertTemplate, pendingKey, createdColumns } = useTemplateColumnInsert({
		listId: listData?.id,
		captureCurrentPosition,
		onInsertToken: handleInsertToken,
	});

	const insertedColumnIds = useMemo(
		() => extractInsertedColumnIds([message, premiumMessage]),
		[message, premiumMessage]
	);

	const rows = useMemo(
		() =>
			buildSnippetRows({
				listColumns: allAiSnippets,
				createdColumns,
				templates: templatesResponse?.data ?? [],
				insertedColumnIds,
			}),
		[allAiSnippets, createdColumns, templatesResponse, insertedColumnIds]
	);

	const inUseCount = useMemo(
		() => rows.filter((row) => row.kind === "listColumn" && row.isInserted).length,
		[rows]
	);

	const handleRowInsert = useCallback(
		(row: SnippetRow) => {
			if (row.kind === "listColumn") {
				handleInsertToken(buildSnippetToken(row.columnId, row.name));

				return;
			}

			void insertTemplate(row.template);
		},
		[handleInsertToken, insertTemplate]
	);

	const handleRowConfigure = useCallback(
		(row: SnippetRow) => {
			handleOpenModal(row.kind === "listColumn" ? row.column : row.template);
		},
		[handleOpenModal]
	);

	const handleColumnRenamed = useCallback(
		(columnId: number, newName: string, rewrite: (text: string) => string) => {
			// 1. Directly mutate the placeholder spans inside every TinyMCE
			//    editor so the visible chip flips to the new name immediately.
			//    This is the most reliable update — it works at the DOM level
			//    and doesn't depend on TinyMCE's setContent / BeforeSetContent
			//    pipeline behaving as expected.
			updateColumnPlaceholdersInTinyMce(columnId, newName);

			// 2. Also run a full setContent round-trip per editor so the
			//    editor's serialized content (returned by `getContent()` on
			//    the next blur) matches the new tokens — and capture the
			//    before/after pairs so we can hand the freshest content to
			//    the parent state.
			const edits = rewriteAllTinyMceEditors(rewrite);

			// Steps 1+2 above keep the visible editor consistent in *every*
			// parent (including the list campaign builder, which doesn't pass
			// withPersistance). The parent-state propagation below is what
			// keeps the saved campaign data in sync — when the parent forwards
			// withPersistance, we use it; otherwise the editor's next blur
			// will write the rewritten content back through handleChange.
			if (!withPersistance) {
				return;
			}

			// 3. Fall back to rewriting the closure values when no editor was
			//    matched — covers tests, collapsed editors, etc.
			const fallbackMessage = rewrite(message);
			const fallbackPremium = rewrite(premiumMessage);

			let resolvedMessage = fallbackMessage;
			let resolvedPremium = fallbackPremium;

			for (const edit of edits) {
				if (premiumMessage && edit.previous === premiumMessage) {
					resolvedPremium = edit.next;
				} else if (message && edit.previous === message) {
					resolvedMessage = edit.next;
				}
			}

			const messageChanged = resolvedMessage !== message;
			const premiumChanged = resolvedPremium !== premiumMessage;

			if (!messageChanged && !premiumChanged) {
				return;
			}

			// 4. Mirror the parent's withPersistance ordering: when a premium
			//    message exists alongside the main message, results[0] is the
			//    premium and results[1] is the main message. Otherwise
			//    results[0] is the main.
			if (premiumMessage) {
				withPersistance([resolvedPremium, resolvedMessage]);
			} else {
				withPersistance([resolvedMessage]);
			}
		},
		[withPersistance, message, premiumMessage]
	);

	return (
		<div className="flex flex-col w-full mt-4">
			<AiSnippetsSectionCard
				inUseCount={inUseCount}
				pendingKey={pendingKey}
				rows={rows}
				onCreateNew={() => handleOpenModal(null)}
				onRowConfigure={handleRowConfigure}
				onRowInsert={handleRowInsert}
			/>

			{listData?.id && (
				<AiSnippetsModal
					allSnippets={allAiSnippets}
					currentMessage={message}
					currentPremiumMessage={premiumMessage}
					initialColumn={modalInitialColumn}
					isOpen={showModal}
					listId={listData.id}
					onClose={handleCloseModal}
					onColumnRenamed={handleColumnRenamed}
					onInsertToken={handleInsertToken}
				/>
			)}
		</div>
	);
});
